import { X, CheckCircle, AlertCircle, AlertTriangle, Info } from 'lucide-react'
import * as React from 'react'
import { createContext, useContext, useState, useCallback } from 'react'

import { cn } from '@/lib/utils'

/**
 * Toast 类型
 */
export type ToastType = 'success' | 'error' | 'warning' | 'info'

/**
 * Toast 配置
 */
export interface ToastConfig {
  /** 类型 */
  type: ToastType
  /** 标题 */
  title: string
  /** 描述 */
  description?: string
  /** 持续时间（毫秒），默认 3000ms */
  duration?: number
  /** 操作按钮 */
  action?: {
    label: string
    onClick: () => void
  }
}

/**
 * Toast 项
 */
interface ToastItem extends ToastConfig {
  id: string
}

/**
 * Toast 上下文
 */
interface ToastContextValue {
  toasts: ToastItem[]
  show: (config: ToastConfig) => void
  success: (message: string, description?: string) => void
  error: (message: string, description?: string) => void
  warning: (message: string, description?: string) => void
  info: (message: string, description?: string) => void
  dismiss: (id: string) => void
  dismissAll: () => void
}

const ToastContext = createContext<ToastContextValue | null>(null)

/**
 * Toast Provider
 */
export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([])

  const dismiss = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id))
  }, [])

  const dismissAll = useCallback(() => {
    setToasts([])
  }, [])

  const show = useCallback(
    (config: ToastConfig) => {
      const id = crypto.randomUUID()
      const duration = config.duration ?? 3000

      setToasts((prev) => [...prev, { ...config, id }])

      if (duration > 0) {
        setTimeout(() => {
          dismiss(id)
        }, duration)
      }
    },
    [dismiss]
  )

  const success = useCallback(
    (message: string, description?: string) => {
      show({ type: 'success', title: message, description })
    },
    [show]
  )

  const error = useCallback(
    (message: string, description?: string) => {
      show({ type: 'error', title: message, description, duration: 5000 })
    },
    [show]
  )

  const warning = useCallback(
    (message: string, description?: string) => {
      show({ type: 'warning', title: message, description })
    },
    [show]
  )

  const info = useCallback(
    (message: string, description?: string) => {
      show({ type: 'info', title: message, description })
    },
    [show]
  )

  return (
    <ToastContext.Provider
      value={{ toasts, show, success, error, warning, info, dismiss, dismissAll }}
    >
      {children}
      <ToastContainer toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  )
}

/**
 * useToast Hook
 */
export function useToast() {
  const context = useContext(ToastContext)
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider')
  }
  return context
}

/**
 * Toast 容器
 */
function ToastContainer({
  toasts,
  onDismiss,
}: {
  toasts: ToastItem[]
  onDismiss: (id: string) => void
}) {
  if (toasts.length === 0) {return null}

  return (
    <div className="fixed bottom-4 right-4 z-50 flex flex-col gap-2 max-w-sm">
      {toasts.map((toast) => (
        <Toast key={toast.id} {...toast} onDismiss={() => onDismiss(toast.id)} />
      ))}
    </div>
  )
}

/**
 * Toast 组件
 */
function Toast({
  type,
  title,
  description,
  action,
  onDismiss,
}: ToastItem & { onDismiss: () => void }) {
  const icons = {
    success: CheckCircle,
    error: AlertCircle,
    warning: AlertTriangle,
    info: Info,
  }

  const colors = {
    success: 'bg-green-50 border-green-200 text-green-800',
    error: 'bg-red-50 border-red-200 text-red-800',
    warning: 'bg-yellow-50 border-yellow-200 text-yellow-800',
    info: 'bg-blue-50 border-blue-200 text-blue-800',
  }

  const iconColors = {
    success: 'text-green-500',
    error: 'text-red-500',
    warning: 'text-yellow-500',
    info: 'text-blue-500',
  }

  const Icon = icons[type]

  return (
    <div
      className={cn(
        'flex items-start gap-3 rounded-lg border p-4 shadow-lg animate-in slide-in-from-right-full',
        colors[type]
      )}
      role="alert"
    >
      <Icon className={cn('h-5 w-5 flex-shrink-0 mt-0.5', iconColors[type])} />
      <div className="flex-1 min-w-0">
        <p className="font-medium">{title}</p>
        {description && (
          <p className="mt-1 text-sm opacity-90">{description}</p>
        )}
        {action && (
          <button
            onClick={action.onClick}
            className="mt-2 text-sm font-medium underline hover:no-underline"
          >
            {action.label}
          </button>
        )}
      </div>
      <button
        onClick={onDismiss}
        className="flex-shrink-0 rounded p-1 hover:bg-black/5 transition-colors"
        aria-label="关闭"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  )
}
